// Reads the OTP cooldown for one key (see redux/otp/otpKeys.ts) and keeps a
// once-a-second countdown running while it is active. The remaining time is
// always derived from the persisted absolute timestamp, so the first render
// after a reload is already correct; the interval only forces re-renders.

import { useEffect, useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { OTP_COOLDOWN_MS } from '@/redux/otp/otpKeys';

export interface OtpCooldown {
  /** true while a matching OTP is still cooling down. */
  active: boolean;
  remainingMs: number;
  /** Remaining time as m:ss, for interpolation into a label — '' when inactive. */
  label: string;
}

// Clamped so a future-dated stamp (clock skew) can never lock the UI for
// longer than one full cooldown.
const remainingFor = (requestedAt: number | undefined): number => {
  if (!requestedAt) return 0;
  return Math.min(Math.max(OTP_COOLDOWN_MS - (Date.now() - requestedAt), 0), OTP_COOLDOWN_MS);
};

const format = (ms: number): string => {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/** Pass `null` while the key is unknown (e.g. no phone typed yet) — inert. */
export default function useOtpCooldown(key: string | null): OtpCooldown {
  const requestedAt = useAppSelector((state) => (key ? state.otp.lastRequestedAt[key] : undefined));
  const remainingMs = remainingFor(requestedAt);

  // The state value is only a re-render trigger; every render re-derives the
  // real remaining time above.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!requestedAt || remainingFor(requestedAt) <= 0) return undefined;
    const id = setInterval(() => {
      setTick((n) => n + 1);
      if (remainingFor(requestedAt) <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [requestedAt]);

  return {
    active: remainingMs > 0,
    remainingMs,
    label: remainingMs > 0 ? format(remainingMs) : '',
  };
}
